// FILE: components/pages/OrgListPage.tsx
"use client";

import { useState, useMemo } from "react";
import { Search, Building2 } from "lucide-react";
import HardNavLink from "@/components/shared/HardNavLink";
import OrgCard from "@/components/cards/OrgCard";
import BackToTop from "@/components/shared/BackToTop";
import type { OrgWithCounts } from "@/actions/orgs";

const SORTS = [
  { value: "name", label: "Tên A-Z" },
  { value: "exams", label: "Nhiều đề thi nhất" },
  { value: "paths", label: "Nhiều lộ trình nhất" },
];

export default function OrgListPage({ initialOrgs }: { initialOrgs: OrgWithCounts[] }) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("name");

  const filtered = useMemo(() => {
    let result = initialOrgs.filter((o) =>
      o.name.toLowerCase().includes(search.toLowerCase())
    );

    if (sort === "exams") {
      result = [...result].sort((a, b) => b.examCount - a.examCount);
    } else if (sort === "paths") {
      result = [...result].sort((a, b) => b.pathCount - a.pathCount);
    } else {
      result = [...result].sort((a, b) => a.name.localeCompare(b.name, "vi"));
    }

    return result;
  }, [initialOrgs, search, sort]);

  return (
    <div className="max-w-[60rem] mx-auto px-6 py-6 max-md:px-3.5">
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-2.5 mb-1">
          <Building2 className="w-5 h-5 text-teal-600" />
          <h1 className="font-display text-xl font-extrabold text-teal-800">Tổ chức nổi bật</h1>
        </div>
        <p className="text-[13px] text-muted-foreground">Khám phá đề thi từ các tổ chức hàng đầu</p>
      </div>

      {/* Sticky filter bar */}
      <div className="sticky top-0 z-20 -mx-6 px-6 py-3 bg-[#f1f5f9]/95 backdrop-blur-sm border-b border-transparent [&.stuck]:border-border [&.stuck]:shadow-[0_2px_8px_rgba(0,0,0,.06)] transition-all mb-5"
        ref={(el) => {
          if (!el) return;
          const observer = new IntersectionObserver(([e]) => {
            el.classList.toggle("stuck", !e.isIntersecting);
          }, { threshold: [1], rootMargin: "-1px 0px 0px 0px" });
          observer.observe(el);
        }}
      >
        <div className="flex items-center gap-2.5 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm tên tổ chức..."
              className="w-full h-9 rounded-full border border-border bg-white pl-9 pr-4 text-[12px] font-medium outline-none focus:border-teal-600 focus:shadow-[0_0_0_3px_rgba(30,90,112,.06)] transition-all placeholder:text-muted-foreground"
            />
          </div>

          {/* Sort */}
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="h-9 rounded-full border border-border bg-white px-3.5 text-[12px] font-semibold text-foreground outline-none cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2364748b%22%20stroke-width%3D%222%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22/%3E%3C/svg%3E')] bg-no-repeat bg-[right_0.75rem_center] focus:border-teal-600 transition-all"
          >
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>

          <span className="text-[11px] text-muted-foreground font-medium ml-auto shrink-0">{filtered.length} tổ chức</span>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-8">
        {filtered.map((o) => (
          <HardNavLink key={o.id} href={`/orgs/${o.slug}`}>
            <OrgCard
              emoji={o.emoji ?? "🏢"}
              name={o.name}
              examCount={o.examCount}
              pathCount={o.pathCount}
            />
          </HardNavLink>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16">
          <div className="text-3xl mb-3">🔍</div>
          <p className="text-sm text-muted-foreground">Không tìm thấy tổ chức nào phù hợp</p>
        </div>
      )}

      <BackToTop />
    </div>
  );
}